import {
  isDevMode,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
  inject,
  provideAppInitializer,
} from "@angular/core";
import { ServiceWorkerModule } from "@angular/service-worker";
import { TranslateModule } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { OAuthModule } from "angular-oauth2-oidc";
import { environment } from "../../environments/environment";
import {
  AuthService,
  PluginInitializerService,
  AuthInterceptor,
} from "core-client/api";
import { CorePluginService, corePluginInitializerFactory } from "core-plugin";
import {
  NgsildConnectorService,
  ngsildConnectorInitializerFactory,
} from "ngsild-connector";
import { WmsChartService, wmsChartInitializerFactory } from "wms-chart-loader";
import { MapService, mapInitializerFactory } from "map";
import { AstimaxService, astimaxInitializerFactory } from "astimax";
import { streamingInitializerFactory, StreamingService } from "streaming";
import {
  OiclOpenbridgeService,
  oicOpenbridgeInitializerFactory,
} from "oicl-openbridge";
import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from "@angular/cdk/overlay";
import localeDe from "@angular/common/locales/de";
import { registerLocaleData } from "@angular/common";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

function authAppInitializerFactory(
  authService: AuthService,
  configurationService: PluginInitializerService,
): () => Promise<void> {
  return () =>
    new Promise<void>((resolve) => {
      const initializeAuth = async () => {
        await authService.initialize();
      };

      configurationService.setup("auth", initializeAuth, resolve);
    });
}

// Import german as language for the angular templates
registerLocaleData(localeDe, "de");

@NgModule({
  declarations: [],
  exports: [TranslateModule],
  imports: [
    BrowserModule,
    /** @TO-DO: Deprecated for V23 **/
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      fallbackLang: "de",
      loader: provideTranslateHttpLoader({
        prefix: "./assets/i18n/",
        suffix: ".json",
      }),
    }),
    OAuthModule.forRoot(),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
  providers: [
    AuthService,
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
  ],
})
export class CoreModule {
  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        {
          provide: "ENVIRONMENT",
          useValue: environment,
          multi: true,
        },
        provideAppInitializer(() => {
          const initializerFn = corePluginInitializerFactory(
            inject(CorePluginService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = ngsildConnectorInitializerFactory(
            inject(NgsildConnectorService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = wmsChartInitializerFactory(
            inject(WmsChartService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = mapInitializerFactory(
            inject(MapService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = astimaxInitializerFactory(
            inject(AstimaxService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = streamingInitializerFactory(
            inject(StreamingService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = oicOpenbridgeInitializerFactory(
            inject(OiclOpenbridgeService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
        provideAppInitializer(() => {
          const initializerFn = authAppInitializerFactory(
            inject(AuthService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
      ],
    };
  }

  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error(
        "CoreModule is already loaded. Import it in the AppModule only",
      );
    }
  }
}
